/**
 * Horizontal pipeline stage stepper — shows progress across all 5 stages.
 */

import { CheckCircle, Clock, AlertCircle, Loader2, HelpCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PipelineStageStatus } from '../types'

const STAGE_LABELS: Record<string, string> = {
  file_registered: 'Registered',
  raw_published: 'Raw Published',
  fhid_assigned: 'FHID Assigned',
  mongo_upserted: 'Mongo Upserted',
  cdc_emitted: 'CDC Emitted',
}

function StageIcon({ status, className }: { status: PipelineStageStatus['status']; className?: string }) {
  const base = cn('h-5 w-5', className)
  switch (status) {
    case 'done':       return <CheckCircle className={cn(base, 'text-green-600')} />
    case 'in_progress': return <Loader2 className={cn(base, 'text-blue-500 animate-spin')} />
    case 'pending':    return <Clock className={cn(base, 'text-gray-400')} />
    case 'error':      return <AlertCircle className={cn(base, 'text-red-500')} />
    default:           return <HelpCircle className={cn(base, 'text-gray-300')} />
  }
}

function stageConnectorColor(status: PipelineStageStatus['status']) {
  return status === 'done' ? 'bg-green-400' : 'bg-gray-200'
}

interface Props {
  stages: PipelineStageStatus[]
}

export function PipelineStageBar({ stages }: Props) {
  return (
    <div className="flex items-start gap-0">
      {stages.map((stage, idx) => (
        <div key={stage.stage} className="flex items-center">
          {/* Stage node */}
          <div className="flex flex-col items-center gap-1 w-24">
            <StageIcon status={stage.status} />
            <span className="text-[10px] text-gray-500 text-center leading-tight">
              {STAGE_LABELS[stage.stage] ?? stage.stage}
            </span>
            {stage.pctComplete != null && stage.status !== 'done' && stage.status !== 'pending' && (
              <span className="text-[10px] font-semibold text-blue-600">{stage.pctComplete}%</span>
            )}
          </div>
          {/* Connector line */}
          {idx < stages.length - 1 && (
            <div className={cn('h-0.5 w-6 mb-5 flex-shrink-0', stageConnectorColor(stage.status))} />
          )}
        </div>
      ))}
    </div>
  )
}
